/**
 * Envío PJ-2 · Biblioteca: todas las marcas y los personajes del usuario, en
 * galerías de tarjetas. Se abre desde la barra vertical del Canvas y desde
 * «Personalización del proyecto» (menú del proyecto).
 *
 *  · Personajes: foto, @, voz (▶), looks y en cuántos proyectos está.
 *  · Marcas: logo, franja de colores, @ y en cuántos proyectos está.
 *  · Cada tarjeta: usar en el proyecto abierto, editar (mismo editor que el
 *    panel de Edición) y eliminar. «Seleccionar» permite borrar varias.
 *  · Eliminar es DEFINITIVO: antes avisa en qué proyectos se usa.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Check, Loader2, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useWorkflowStore } from '../../store/workflowStore';
import { useBrandStore } from '../../store/brandStore';
import { useCharacterStore } from '../../store/characterStore';
import { brandService } from '../../services/brandService';
import { characterService } from '../../services/characterService';
import { CharacterEditor } from '../timeline/CharacterPanel';
import { BrandEditor } from '../timeline/BrandPanel';
import VoicePreviewButton from '../nodes/VoicePreviewButton';
import { canPreview } from '../../utils/elevenVoices';
import { libraryCounts, matchesQuery, usageText } from './libraryModel';

const TABS = [['personajes', 'Personajes'], ['marcas', 'Marcas']];
const errText = (err) => err?.response?.data?.detail || 'No se pudo completar';

const IconBtn = ({ label, onClick, danger = false, children, testId }) => (
  <button type="button" className={`lib-icon ${danger ? 'is-danger' : ''}`} onClick={onClick}
    aria-label={label} title={label} data-testid={testId}>
    {children}
  </button>
);

const CharacterCard = ({ c, inProject, selecting, selected, onToggle, onUse, onEdit, onDelete }) => (
  <article className={`lib-card ${selected ? 'is-selected' : ''}`} data-testid="lib-character-card">
    <button type="button" className="lib-card-media is-portrait" onClick={selecting ? onToggle : onEdit}
      aria-label={selecting ? `Seleccionar ${c.name}` : `Editar ${c.name}`}>
      {c.cover_url ? <img src={c.cover_url} alt="" loading="lazy" /> : <span>{(c.name || '?').slice(0, 1)}</span>}
      {selecting && <i className="lib-check" aria-hidden="true">{selected && <Check size={12} />}</i>}
    </button>
    <div className="lib-card-body">
      <b>{c.name}</b>
      <small>@{c.handle}</small>
      <small className="lib-usage">{usageText(c.project_count, inProject, 'reparto')}{c.looks ? ` · ${c.looks} look${c.looks > 1 ? 's' : ''}` : ''}{c.needs_consent ? ' · falta autorización' : ''}</small>
      {!selecting && (
        <div className="lib-actions">
          {canPreview(c.voice) && <VoicePreviewButton voice={c.voice.voice} testId={`lib-voice-${c.handle}`} />}
          {onUse && !inProject && <IconBtn label="Sumar al reparto del proyecto" onClick={onUse} testId="lib-use"><Plus size={15} /></IconBtn>}
          <IconBtn label="Editar" onClick={onEdit} testId="lib-edit"><Pencil size={14} /></IconBtn>
          <IconBtn label="Eliminar" onClick={onDelete} danger testId="lib-delete"><Trash2 size={14} /></IconBtn>
        </div>
      )}
    </div>
  </article>
);

const BrandCard = ({ b, inProject, selecting, selected, onToggle, onUse, onUnassign, onEdit, onDelete }) => (
  <article className={`lib-card ${selected ? 'is-selected' : ''}`} data-testid="lib-brand-card">
    <button type="button" className="lib-card-media is-logo" onClick={selecting ? onToggle : onEdit}
      aria-label={selecting ? `Seleccionar ${b.name}` : `Editar ${b.name}`}>
      {b.logo_url ? <img src={b.logo_url} alt="" loading="lazy" /> : <span>{(b.name || '?').slice(0, 1)}</span>}
      {selecting && <i className="lib-check" aria-hidden="true">{selected && <Check size={12} />}</i>}
    </button>
    <div className="lib-strip" aria-hidden="true">
      {(b.colors || []).map((hx, i) => <i key={`${hx}-${i}`} style={{ background: hx, flex: i === 0 ? 3 : 1 }} />)}
    </div>
    <div className="lib-card-body">
      <b>{b.name}</b>
      <small>@{b.handle}</small>
      <small className="lib-usage">{usageText(b.project_count, inProject, 'marca')}</small>
      {!selecting && (
        <div className="lib-actions">
          {onUse && !inProject && <IconBtn label="Usar en este proyecto" onClick={onUse} testId="lib-use"><Plus size={15} /></IconBtn>}
          {onUnassign && inProject && <IconBtn label="Quitar de este proyecto" onClick={onUnassign} testId="lib-unassign"><X size={14} /></IconBtn>}
          <IconBtn label="Editar" onClick={onEdit} testId="lib-edit"><Pencil size={14} /></IconBtn>
          <IconBtn label="Eliminar" onClick={onDelete} danger testId="lib-delete"><Trash2 size={14} /></IconBtn>
        </div>
      )}
    </div>
  </article>
);

/* Confirmación de borrado definitivo, dentro de la ventana. */
const ConfirmDelete = ({ confirm, busy, onCancel, onConfirm }) => {
  const { kind, items, projects, total } = confirm;
  const one = items.length === 1;
  const noun = kind === 'marcas' ? (one ? 'la marca' : 'marcas') : (one ? 'el personaje' : 'personajes');
  const effect = kind === 'marcas'
    ? 'Esos proyectos van a quedar sin marca. El logo y las imágenes siguen en tu Galería.'
    : 'Sale del reparto de esos proyectos. Sus fotos siguen en tu Galería y lo ya generado no cambia.';
  return (
    <div className="lib-confirm" role="alertdialog" aria-modal="true" aria-labelledby="lib-confirm-title" data-testid="lib-confirm">
      <div className="lib-confirm-box">
        <h3 id="lib-confirm-title">{one ? `¿Eliminar ${noun} «${items[0].name}»?` : `¿Eliminar ${items.length} ${noun}?`}</h3>
        {projects === null
          ? <p><Loader2 size={13} className="spin" /> Revisando dónde se usa…</p>
          : !one && total > 0
            ? <p>Entre {kind === 'marcas' ? 'todas' : 'todos'} se usan en {total} proyecto{total === 1 ? '' : 's'}. {effect}</p>
          : projects.length > 0
            ? <p>Se usa en {projects.length === 1 ? 'el proyecto' : `${projects.length} proyectos`}: <b>{projects.slice(0, 4).join(', ')}{projects.length > 4 ? '…' : ''}</b>. {effect}</p>
            : <p>No se usa en ningún proyecto. {kind === 'marcas' ? 'El logo y las imágenes siguen en tu Galería.' : 'Sus fotos siguen en tu Galería.'}</p>}
        <p className="lib-confirm-warn">Esta acción no se puede deshacer.</p>
        <div className="lib-confirm-actions">
          <button type="button" className="lib-btn" onClick={onCancel} disabled={busy} data-testid="lib-confirm-cancel">Cancelar</button>
          <button type="button" className="lib-btn is-danger" onClick={onConfirm} disabled={busy || projects === null} data-testid="lib-confirm-ok">
            {busy ? <Loader2 size={13} className="spin" /> : <Trash2 size={13} />} Eliminar
          </button>
        </div>
      </div>
    </div>
  );
};

export default function LibraryWindow({ initialTab = 'personajes', onClose }) {
  const projectId = useWorkflowStore((s) => s.currentProjectId);
  const projectBrand = useBrandStore((s) => s.brand);
  const cast = useCharacterStore((s) => s.cast);
  const [tab, setTab] = useState(initialTab === 'marcas' ? 'marcas' : 'personajes');
  const [query, setQuery] = useState('');
  const [data, setData] = useState({ personajes: null, marcas: null });
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [editing, setEditing] = useState(null);       // {kind, id}
  const [brandEdit, setBrandEdit] = useState(null);   // {brand, card}
  const [confirm, setConfirm] = useState(null);       // {kind, items, projects}
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState('');

  const reload = useCallback(async () => {
    const [personajes, marcas] = await Promise.all([
      characterService.list().catch(() => []),
      brandService.list().catch(() => []),
    ]);
    setData({ personajes, marcas });
  }, []);

  // Lo del proyecto abierto (para marcar «en este proyecto»).
  const refreshProject = useCallback(() => {
    if (!projectId) return;
    useBrandStore.getState().load(projectId, { force: true });
    useCharacterStore.getState().loadCast(projectId, { force: true });
  }, [projectId]);

  useEffect(() => { reload(); refreshProject(); }, [reload, refreshProject]);
  useEffect(() => { setSelected(new Set()); setSelecting(false); setQuery(''); }, [tab]);
  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape' && !confirm) onClose?.(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose, confirm]);

  const castIds = useMemo(() => new Set((cast || []).map((c) => c.id)), [cast]);
  const list = (data[tab] || []).filter((it) => matchesQuery(it, query));
  const counts = libraryCounts(data);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); } catch (err) { toast.error(errText(err)); } finally { setBusy(false); }
  };

  const toggle = (id) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const askDelete = async (kind, items) => {
    setConfirm({ kind, items, projects: null });
    if (items.length === 1) {
      try {
        const out = kind === 'marcas' ? await brandService.get(items[0].id) : await characterService.get(items[0].id);
        setConfirm({ kind, items, projects: (out.used_in_projects || []).map((p) => p.name) });
      } catch { setConfirm({ kind, items, projects: [] }); }
    } else {
      // Varias: alcanza con el total (los nombres van en el aviso final).
      const total = items.reduce((n, it) => n + (it.project_count || 0), 0);
      setConfirm({ kind, items, projects: [], total });
    }
  };

  const doDelete = () => run(async () => {
    const { kind, items } = confirm;
    const remove = kind === 'marcas' ? brandService.remove : characterService.remove;
    let ok = 0;
    for (const it of items) {
      try { await remove(it.id); ok += 1; } catch (err) { toast.error(`${it.name}: ${errText(err)}`); }
    }
    setConfirm(null);
    setSelected(new Set());
    setSelecting(false);
    if (editing) setEditing(null);
    await reload();
    refreshProject();
    if (ok) toast.success(ok === 1 ? `«${items[0].name}» eliminado` : `${ok} eliminados`);
  });

  const openEdit = (kind, id) => run(async () => {
    if (kind === 'marcas') setBrandEdit(await brandService.get(id));
    else await useCharacterStore.getState().open(id);
    setEditing({ kind, id });
  });

  const closeEdit = () => {
    setEditing(null);
    setBrandEdit(null);
    useCharacterStore.getState().close();
    reload();
    refreshProject();
  };

  const addCharacter = (c) => run(async () => {
    await useCharacterStore.getState().addToCast(projectId, c.id);
    await reload();
  }, `@${c.handle} sumado al reparto`);
  const assignBrand = (b) => run(async () => {
    await useBrandStore.getState().assign(projectId, b.id);
    await reload();
  }, `Marca @${b.handle} asignada al proyecto`);
  const unassignBrand = () => run(async () => {
    await useBrandStore.getState().assign(projectId, null);
    await reload();
  }, 'El proyecto quedó sin marca');

  const create = () => run(async () => {
    const name = newName.trim();
    if (tab === 'marcas') {
      const out = await brandService.create({ name });
      setNewName('');
      await reload();
      setBrandEdit(out);
      setEditing({ kind: 'marcas', id: out.brand.id });
    } else {
      const out = await characterService.create({ name });
      setNewName('');
      await reload();
      await useCharacterStore.getState().open(out.character.id);
      setEditing({ kind: 'personajes', id: out.character.id });
    }
  }, tab === 'marcas' ? 'Marca creada' : 'Personaje creado');

  const editedBrandIsProject = brandEdit && projectBrand?.id === brandEdit.brand?.id;
  const brandUpdate = async (patch) => {
    const out = await brandService.update(brandEdit.brand.id, patch);
    setBrandEdit(out);
    if (editedBrandIsProject) refreshProject();
  };
  const brandUndo = async () => {
    const out = await brandService.undo(brandEdit.brand.id);
    setBrandEdit(out);
    if (editedBrandIsProject) refreshProject();
  };

  return (
    <div className="gallery-overlay lib-overlay" data-testid="library-window" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="gallery-panel lib-panel" role="dialog" aria-modal="true" aria-label="Biblioteca">
        <div className="gallery-header lib-header">
          {editing ? (
            <button type="button" className="lib-back" onClick={closeEdit} data-testid="lib-back">
              <ArrowLeft size={15} /> Biblioteca
            </button>
          ) : (
            <div className="lib-title">
              <b>Biblioteca</b>
              <div className="lib-tabs" role="tablist" aria-label="Qué mostrar">
                {TABS.map(([k, label]) => (
                  <button key={k} type="button" role="tab" aria-selected={tab === k}
                    className={tab === k ? 'is-active' : ''} onClick={() => setTab(k)} data-testid={`lib-tab-${k}`}>
                    {label}{counts[k] != null && <em>{counts[k]}</em>}
                  </button>
                ))}
              </div>
            </div>
          )}
          <button className="gallery-close-btn" onClick={onClose} aria-label="Cerrar" data-testid="lib-close"><X size={18} /></button>
        </div>

        {editing ? (
          <div className="lib-edit-scroll">
            <div className="tl-x lib-editor">
              {editing.kind === 'personajes' ? (
                <div className="tl-inspector tl-brand-panel tl-char-panel">
                  <CharacterEditor projectId={projectId} onBack={closeEdit} backLabel="Biblioteca"
                    canRemove={castIds.has(editing.id)} />
                </div>
              ) : (
                <div className="tl-inspector tl-brand-panel">
                  <BrandEditor
                    brand={brandEdit?.brand}
                    logoUrl={brandEdit?.card?.logo_url}
                    update={brandUpdate}
                    undo={brandUndo}
                    actions={projectId && !editedBrandIsProject ? (
                      <button type="button" className="tl-brand-link" onClick={() => assignBrand(brandEdit.brand)} data-testid="lib-edit-use">
                        Usar en este proyecto
                      </button>
                    ) : null}
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="lib-toolbar">
              <label className="lib-search">
                <Search size={14} aria-hidden="true" />
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={tab === 'marcas' ? 'Buscar marca' : 'Buscar personaje'} data-testid="lib-search" />
              </label>
              {selecting ? (
                <>
                  <span className="lib-sel-count">{selected.size} seleccionad{selected.size === 1 ? 'a' : 'as'}</span>
                  <button type="button" className="lib-btn is-danger" disabled={!selected.size || busy}
                    onClick={() => askDelete(tab, (data[tab] || []).filter((it) => selected.has(it.id)))} data-testid="lib-bulk-delete">
                    <Trash2 size={13} /> Eliminar
                  </button>
                  <button type="button" className="lib-btn" onClick={() => { setSelecting(false); setSelected(new Set()); }}>Listo</button>
                </>
              ) : (
                <button type="button" className="lib-btn" disabled={!(data[tab] || []).length} onClick={() => setSelecting(true)} data-testid="lib-select">Seleccionar</button>
              )}
            </div>

            <div className="lib-grid-scroll">
              {data[tab] === null ? (
                <p className="lib-empty"><Loader2 size={16} className="spin" /> Cargando…</p>
              ) : (
                <div className={`lib-grid ${tab === 'marcas' ? 'is-brands' : ''}`} data-testid="lib-grid">
                  {!selecting && (
                    <div className="lib-card lib-new">
                      <input value={newName} onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && newName.trim()) create(); }}
                        placeholder={tab === 'marcas' ? 'Nombre de la marca' : 'Nombre del personaje'} data-testid="lib-new-name" />
                      <button type="button" className="lib-btn" disabled={busy || !newName.trim()} onClick={create} data-testid="lib-create">
                        <Plus size={13} /> {tab === 'marcas' ? 'Nueva marca' : 'Nuevo personaje'}
                      </button>
                      {tab === 'personajes' && <small>O pedíselo a LMX con una foto 📎.</small>}
                    </div>
                  )}
                  {tab === 'personajes' && list.map((c) => (
                    <CharacterCard key={c.id} c={c} inProject={castIds.has(c.id)}
                      selecting={selecting} selected={selected.has(c.id)} onToggle={() => toggle(c.id)}
                      onUse={projectId ? () => addCharacter(c) : null}
                      onEdit={() => openEdit('personajes', c.id)}
                      onDelete={() => askDelete('personajes', [c])} />
                  ))}
                  {tab === 'marcas' && list.map((b) => (
                    <BrandCard key={b.id} b={b} inProject={projectBrand?.id === b.id}
                      selecting={selecting} selected={selected.has(b.id)} onToggle={() => toggle(b.id)}
                      onUse={projectId ? () => assignBrand(b) : null}
                      onUnassign={projectId ? unassignBrand : null}
                      onEdit={() => openEdit('marcas', b.id)}
                      onDelete={() => askDelete('marcas', [b])} />
                  ))}
                </div>
              )}
              {data[tab] && !list.length && query && <p className="lib-empty">Nada coincide con «{query}».</p>}
            </div>
          </>
        )}

        {confirm && (
          <ConfirmDelete confirm={confirm} busy={busy} onCancel={() => setConfirm(null)} onConfirm={doDelete} />
        )}
      </div>
    </div>
  );
}
